import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl, fetchJson } from '../../apiBase';

export const importFailureMessage = (code) => ({
  import_timeout: 'Dosyanın işlenmesi süre sınırını aştı. Önceki sonuçlar korundu. Dosyayı kontrol edip tekrar yükleyebilirsiniz.',
  import_interrupted: 'Dosya işleme kesintiye uğradı. Önceki sonuçlar korundu. Tekrar yükleyebilirsiniz.',
  import_worker_died: 'Dosya işleme tamamlanamadı. Önceki sonuçlar korundu. Tekrar yükleyebilirsiniz.',
}[code] || 'Dosya okunamadı veya sonuçlar kaydedilemedi. Önceki sonuçlar korundu. Dosyayı kontrol edip tekrar yükleyebilirsiniz.');

export default function useExamImport(schoolId, examId, onComplete) {
  const [state, setState] = useState({ status: 'idle' });
  const [revision, setRevision] = useState(0);
  const completed = useRef(null);
  const callback = useRef(onComplete);
  callback.current = onComplete;
  const refresh = useCallback(() => {
    // The same successful file is idempotent on the server; explicitly show
    // its stored result again after a deliberate re-upload.
    completed.current = null;
    setRevision((value) => value + 1);
  }, []);
  const scope = `${schoolId}/${examId}`;

  useEffect(() => {
    if (!schoolId || !examId) {
      setState({ status: 'idle', scope });
      return undefined;
    }
    let active = true;
    let timer;
    let controller;
    setState({ status: 'checking', scope });
    const poll = async () => {
      controller = new AbortController();
      const requestDeadline = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(examId)}/import-status`), {
          signal: controller.signal,
          dedupe: false,
        });
        if (!response.ok) throw new Error('status_unavailable');
        const job = await response.json();
        if (!active) return;
        setState({ ...job, scope });
        if (job.status === 'succeeded' && job.job_id !== completed.current) {
          completed.current = job.job_id;
          callback.current?.(job.result);
        }
        if (job.status === 'queued' || job.status === 'running') timer = setTimeout(poll, 2000);
      } catch (_) {
        if (!active) return;
        // A disconnected browser cannot decide whether the server committed.
        setState((previous) => ({ ...previous, status: 'checking', scope, connectionLost: true }));
        timer = setTimeout(poll, 4000);
      } finally {
        clearTimeout(requestDeadline);
      }
    };
    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
      controller?.abort();
    };
  }, [schoolId, examId, revision, scope]);

  const current = state.scope === scope ? state : { status: examId ? 'checking' : 'idle' };
  return { ...current, refresh, busy: ['checking', 'queued', 'running'].includes(current.status) };
}
